import React, {useState, useEffect} from 'react'

import BtnExcel from '../../Components/BtnExcel';
import Navbar from '../../Components/Navbar/Navbar'
import AntDTable from '../../Components/Table/AntDTable';
import dayjs from 'dayjs';

import { 
  Layout,
  Col, 
  Row,
  Button,
  Dropdown, 
  Space,
  Modal,
  Input,
  Tabs,
  DatePicker,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { 
  ExclamationCircleFilled,
  QuestionCircleOutlined,
} from '@ant-design/icons';import { MoreVert  } from '@mui/icons-material';
import { UseGetRequest } from '../../Utils/api/ApiRequests'
import { usePostRequest } from '../../hooks/usePostRequest'
import { 
  formatDate, 
  formatAmount, 
  getTodayDateISO, 
  refreshPage,
  getStartDate, 
  getEndDate, 
  getTabkey 
} from '../../Utils/Common';

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { Search } = Input;
const { confirm } = Modal;

const Payment = () => {
  const start_date = getStartDate();
  const end_date = getEndDate();
  const tab_key = getTabkey();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [pageSize, setPageSize] = useState('');
  const [visible, setVisible] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [paymentId, setPaymentId] = useState('');
  const [remarks, setRemarks] = useState('')
  const [totalAmount, setTotalAmount] = useState(0);
  const [paymentData, setPaymentData] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const startDate = dayjs().startOf('month');
  const endDate = dayjs().endOf('month');

  const [filterConfig, setFilterConfig] = useState({
    payment_id: "",
    type: "tenant",
    start_date: start_date || '',
    end_date: end_date || '',
    payment_status: tab_key ? tab_key : "for_approval",
    page: 1,
  })

  const { callPost, loading } = usePostRequest(()=>{
    fetchTenantPayment();
    setVisible(false);
    refreshPage();
  });

  const customTitle = (
    <div style={{ display: 'flex', alignItems: 'center'}}>
      <ExclamationCircleFilled style={{ color: 'red', marginRight: 8, fontSize: '2rem'}} />
      <span>Permanently removing this Tenant Payment<br/>Are you sure you want to continue?</span>
    </div>
  );

  const handleOk = () => {
    callPost(
      "payments/delete", 
      {
        payment_id: paymentId,
        delete_reason: remarks,
      },
      false
    );
  };

  function ActionBtn(row) {
    const items = [
      {
        label: (
          <div onClick={()=> navigate('/preview-payment/tenant/' + row.row.id)}>
            Print Preview
          </div>
        ),
        key: '1',
      },
      (row.row.payment_status === "for_approval") && {
        label: (
          <div 
            onClick={() => showReviewConfirm(row.row.id)}
            style={{ color: 'blue' }}
          >
            Review
          </div>
        ),
        key: 'review',
      },
      (row.row.payment_status === "bounced") && {
        label: (
          <div 
            onClick={() => showBouncedConfirm(row.row.id)}
            style={{ color: 'blue' }}
          >
            Return To For Approval
          </div>
        ),
        key: 'review',
      },
      (row.row.payment_status !== "voided") && {
        label: (
          <div style={{color: 'red'}} onClick={() => showDeleteConfirm(row.row.id)}>
            Void Payment
          </div>
        ),
        key: '7',
      },
    ];

    return (
      <span className='center'>
        <Dropdown
          menu={{
          items,
          }}
          trigger={['click']}
        >
          <div type='primary' onClick={(e) => e.preventDefault()} className='action-btn'>
            <Space>
              <MoreVert />
            </Space>
          </div>
        </Dropdown>
      </span>
    );
  };

  const handleRemarks = (info) => {
    setRemarks(info)
    setIsDisabled(info.trim() === '')
  };

  const handleButtonClick = (id) => {
    const url = '/preview-billing/tenant/' + id;
    window.open(url, '_blank');
  };
  
  async function fetchTenantPayment() {
    setIsLoading(true)
    const response = await UseGetRequest("payments/get", filterConfig);
    if (response?.data) {
      const billing_data = response?.data?.data?.map((data) => ({
        id: parseInt(data.id),
        tenant_id: data.tenant_id,
        owner_id: data.owner_id,
        owner_name: data.owner_name,
        tenant_name: data.tenant_name,
        payment_date: formatDate(data.payment_date),
        encoded_date: formatDate(data.encoded_on),
        amount: formatAmount(data.grand_total),
        remarks: data.remarks,
        prepared_by: data.prepared_by,
        type: data.type,
        delete_reason: data.delete_reason,
        grand_total: data.grand_total,
        payment_type: data.payment_type,
        payment_status: data.payment_status,
        bank_name: data.bank_name,
        check_number: data.check_number,
        check_date: data.check_date,
        other_type: data.other_type,
        reference_no: data.reference_no,
        issued_by: data.issued_by,
        concatenated_property: data.concatenated_property,
        billings: data?.billings?.map((data) => (
          <Button className='mb-2' onClick={() => handleButtonClick(data.id)}>{data.doc_no}</Button>
        ))
      }))

      setTotalAmount(response?.data?.total)
      setPaymentData(billing_data);
      setPageSize(response?.data?.size)
    } else {
      setTotalAmount(0)
      setPaymentData([]);
      setPageSize('')
    }
    setIsLoading(false)
  };

  async function fetchExportData() {
    setExportLoading(true)
    const response = await UseGetRequest("payments/get", 
      {
        payment_id: filterConfig.payment_id,
        type: filterConfig.type,
        start_date: filterConfig.start_date,
        end_date: filterConfig.end_date,
        payment_status: filterConfig.payment_status,
      }
    );
    if (response?.data) {
      const excel_data = response.data.data.map((data) => ({
        id: data.id,
        owner_name: data.owner_name,
        tenant_name: data.tenant_name,
        concatenated_property: data.concatenated_property,
        payment_date: data.payment_date && formatDate(data.payment_date),
        encoded_date: data.encoded_on && formatDate(data.encoded_on),
        remarks: data.remarks,
        prepared_by: data.prepared_by,
        type: data.type,
        grand_total: data.grand_total && formatAmount(data.grand_total),
        payment_type: data.payment_type,
        payment_status: data.payment_status,
        bank_name: data.bank_name,
        check_number: data.check_number,
        check_date: data.check_date && formatDate(data.check_date),
        other_type: data.other_type,
        reference_no: data.reference_no,
        issued_by: data.issued_by,
      }))

      setExcelData(excel_data)
      setExportLoading(false);
    } else {
      setExcelData([])
      setExportLoading(false);
    }
  };

  const onChange = (activeKey) => {
    localStorage.setItem('tabkey', JSON.stringify(activeKey));
    if(activeKey === "cleared" || activeKey === "all"){
      setFilterConfig((prev) => (
        {
          ...prev,
          payment_status:  activeKey,
          start_date: dayjs(startDate).format('YYYY-MM-DD'),
          end_date: dayjs(endDate).format('YYYY-MM-DD')
        }
      ));
      localStorage.setItem('start_date', JSON.stringify(startDate.format('YYYY-MM-DD')));
      localStorage.setItem('end_date', JSON.stringify(endDate.format('YYYY-MM-DD')));
    } else {
      setFilterConfig((prev) => (
        {
          ...prev,
          payment_status:  activeKey,
          start_date: '',
          end_date: ''
        }
      ));
      localStorage.removeItem('start_date');
      localStorage.removeItem('end_date');
    }
  };

  const showDeleteConfirm = (id) => {
    setVisible(true);
    setPaymentId(id)
  };

  const showReviewConfirm = (id) => {
    confirm({
      title: `Confirm Payment`,
      icon: <QuestionCircleOutlined/>,
      content: 'This action cannot be undone.',
      okText: 'Approve',
      cancelText: 'Disapprove',
      closable: true,
      okButtonProps: {
        className: 'review-yes-button',
      },
      cancelButtonProps: {
        className: 'review-no-button',
      },
      confirmLoading: loading,
      onOk() {
        callPost(
          "payments/check_action", 
          {
            payment_id: id,
            action: 'approve',
          },
          false
        );
      },
      onCancel() {
        callPost(
          "payments/check_action", 
          {
            payment_id: id,
            action: 'bounce',
          },
          false
        );
      },
    });
  };

  const showBouncedConfirm = (id) => {
    confirm({
      title: `Confirm Return To Payment`,
      icon: <QuestionCircleOutlined/>,
      content: 'This action cannot be undone',
      okText: 'Approve',
      cancelText: 'Disapprove',
      closable: true,
      okButtonProps: {
        className: 'review-yes-button',
      },
      cancelButtonProps: {
        className: 'review-no-button',
      },
      confirmLoading: loading,
      onOk() {
        callPost(
          "payments/check_action", 
          {
            payment_id: id,
            action: 'for_approval',
          },
          false
        );
      },
    });
  };

  const onSearchDate = (e) => {
    if(e){
      setFilterConfig((prev) => (
        {
          ...prev,
          start_date: e[0].format("YYYY-MM-DD"),
          end_date: e[1].format("YYYY-MM-DD"),
          page: 1,
        }
      ));
      localStorage.setItem('start_date', JSON.stringify(e[0].format("YYYY-MM-DD")));
      localStorage.setItem('end_date', JSON.stringify(e[1].format("YYYY-MM-DD")));
    } else {
      setFilterConfig((prev) => (
        {
          ...prev,
          start_date: '',
          end_date: '',
          page: 1,
        }
      ));
      localStorage.removeItem('start_date');
      localStorage.removeItem('end_date');
    }
  };

  const handleSearchChange = (e) => {
    setFilterConfig((prev) => (
      {
        ...prev,
        search: e || '',
        page: 1,
      }
    ));
  };

  const handleEmpty = (e) => {
    if(e.target.value === ''){
      setFilterConfig((prev) => (
        {
          ...prev,
          search: e.target.value || '',
          page: 1,
        }
      ));
    }
  }; 

  const handleTableChange = (pagination) => {
    setFilterConfig((prev) => ({
      ...prev,
      page: parseInt(pagination.current),
      page_size: pagination.pageSize
      // total_records_per_page: pagination.pageSize,
    }));
  };

  useEffect(() => {
    fetchTenantPayment();
    fetchExportData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig]);
  
  return (
    <Layout className='page'>
      <Navbar navKey={'tenant_payment_nav'}/>
      <Layout className='content-container'>
        <Row className='custom-padding responsive-inline responsive-tab-inline'>
          <Col span={12} className='header-container'>
              TENANT PAYMENT
          </Col>
          <Col span={12} className='mt-2 responsive-tab-width' align='end'>
            <Space className='responsive-inline'>
              <BtnExcel
                label={"Export to Excel"}
                size={"sm"}
                excelData={excelData}
                xlsxHeaders={[
                  "ID",
                  "Owner Name",
                  "Tenant Name",
                  "Building & Unit Number",
                  "Payment Date",
                  "Encoded Date",
                  "Remarks",
                  "Prepared By",
                  "Type",
                  "Grand Total",
                  "Payment Type",
                  "Payment Status",
                  "Bank Name",
                  "Check Number",
                  "Check Date",
                  "Other Type",
                  "Reference Number",
                  "Issued By",
                ]}
                handleData={fetchExportData}
                isClicked={exportLoading}
                fileName={`Tenant_Payment_${getTodayDateISO()}.xlsx`}
              /> 
              <Button 
                className='button-color responsive-header-btn' 
                onClick={() => navigate('/add-payment/tenant')} 
                type="primary"
              >
                + Add New Payment
              </Button>
              </Space>
          </Col>
        </Row>
        <Row>
          <Space className='responsive-inline excel-row'>
            <Search
              placeholder="Search..."
              size='medium'
              onSearch={(e)=>handleSearchChange(e)}
              onChange={(e)=>handleEmpty(e)}
              className='responsive-header-input'
            />   
            <RangePicker 
              renderExtraFooter={() => 'extra footer'} 
              className='responsive-header-input'
              value={[
                filterConfig?.start_date ? dayjs(filterConfig.start_date) : null,
                filterConfig?.end_date ? dayjs(filterConfig.end_date) : null
              ]}
              onChange={(e)=>onSearchDate(e)}
            />
          </Space>
        </Row>
        <Row gutter={[16, 16]} className='mx-4'>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}  className='amount-total' align='right'>
            Total Amount: {formatAmount(totalAmount)}
          </Col>
        </Row>
        <Row className='page-content'>
          <Col span={24} className='responsive-overflow-auto'>
            <Tabs 
              type="card" 
              className="custom-tabs"
              activeKey={filterConfig?.payment_status}
              onChange={onChange}
            >
              <TabPane tab="All" key="all">
                <AntDTable
                  tableHeaders={[
                    "Payment Number",
                    "Building & Unit Number",
                    "Tenant TradeName",
                    "Payment Date",
                    "Encoded Date",
                    "Paid Amount",
                    "Billings",
                    "Remarks",
                    "Issued By",
                    "Payment Status",
                    "Payment Type",
                    "Void Reason",
                  ]}
                  headerSelector={[
                    "id",
                    "concatenated_property",
                    "tenant_name",
                    "payment_date",
                    "encoded_date",
                    "amount",
                    "billings",
                    "remarks",
                    "issued_by",
                    "payment_status",
                    "payment_type",
                    "delete_reason",
                  ]}
                  tableData={paymentData}
                  ActionBtn={(row) => ActionBtn(row)}
                  withAction={true}
                  loading={isLoading}
                  tablePages={10}
                  handleTableChange={handleTableChange}
                  filterConfig={filterConfig}
                  totalSize={pageSize}
                  isSearch={true}
                />
              </TabPane>
              <TabPane tab="For Approval" key="for_approval">
                <AntDTable
                  tableHeaders={[
                    "Payment Number",
                    "Building & Unit Number",
                    "Tenant TradeName",
                    "Payment Date",
                    "Encoded Date",
                    "Paid Amount",
                    "Billings",
                    "Remarks",
                    "Issued By",
                  ]}
                  headerSelector={[
                    "id",
                    "concatenated_property",
                    "tenant_name",
                    "payment_date",
                    "encoded_date",
                    "amount",
                    "billings",
                    "remarks",
                    "issued_by",
                  ]}
                  tableData={paymentData}
                  ActionBtn={(row) => ActionBtn(row)}
                  withAction={true}
                  loading={isLoading}
                  tablePages={10}
                  handleTableChange={handleTableChange}
                  filterConfig={filterConfig}
                  totalSize={pageSize}
                  isSearch={true}
                />
              </TabPane>
              <TabPane tab="Bounced" key="bounced">
                <AntDTable
                  tableHeaders={[
                    "Payment Number",
                    "Building & Unit Number",
                    "Tenant TradeName",
                    "Payment Date",
                    "Encoded Date",
                    "Paid Amount",
                    "Billings",
                    "Remarks",
                    "Issued By",
                  ]}
                  headerSelector={[
                    "id",
                    "concatenated_property",
                    "tenant_name",
                    "payment_date",
                    "encoded_date",
                    "amount",
                    "billings",
                    "remarks",
                    "issued_by",
                  ]}
                  tableData={paymentData}
                  ActionBtn={(row) => ActionBtn(row)}
                  withAction={true}
                  loading={isLoading}
                  tablePages={10}
                  handleTableChange={handleTableChange}
                  filterConfig={filterConfig}
                  totalSize={pageSize}
                  isSearch={true}
                />
              </TabPane>
              <TabPane tab="Cleared" key="cleared">
                <AntDTable
                  tableHeaders={[
                    "Payment Number",
                    "Building & Unit Number",
                    "Tenant TradeName",
                    "Payment Date",
                    "Encoded Date",
                    "Paid Amount",
                    "Billings",
                    "Remarks",
                    "Issued By",
                    "Payment Type",
                  ]}
                  headerSelector={[
                    "id",
                    "concatenated_property",
                    "tenant_name",
                    "payment_date",
                    "encoded_date",
                    "amount",
                    "billings",
                    "remarks",
                    "issued_by",
                    "payment_type"
                  ]}
                  tableData={paymentData}
                  ActionBtn={(row) => ActionBtn(row)}
                  withAction={true}
                  loading={isLoading}
                  tablePages={10}
                  handleTableChange={handleTableChange}
                  filterConfig={filterConfig}
                  totalSize={pageSize}
                  isSearch={true}
                />
              </TabPane>
              <TabPane tab="Voided" key="voided">
                <AntDTable
                  tableHeaders={[
                    "Payment Number",
                    "Building & Unit Number",
                    "Tenant TradeName",
                    "Payment Date",
                    "Encoded Date",
                    "Paid Amount",
                    "Billings",
                    "Remarks",
                    "Issued By",
                    "Payment Type",
                    "Void Reason",
                  ]}
                  headerSelector={[
                    "id",
                    "concatenated_property",
                    "tenant_name",
                    "payment_date",
                    "encoded_date",
                    "amount",
                    "billings",
                    "remarks",
                    "issued_by",
                    "payment_type",
                    "delete_reason",
                  ]}
                  tableData={paymentData}
                  ActionBtn={(row) => ActionBtn(row)}
                  withAction={false}
                  loading={isLoading}
                  tablePages={10}
                  handleTableChange={handleTableChange}
                  filterConfig={filterConfig}
                  totalSize={pageSize}
                  isSearch={true}
                />
              </TabPane>
            </Tabs>
          </Col>
        </Row>
      </Layout>
      <Modal
        title={customTitle}
        open={visible}
        icon={<ExclamationCircleFilled style={{ color: 'red' }} />}
        onOk={handleOk}
        okText="Yes"
        okType="danger"
        cancelText="No"
        okButtonProps={{ disabled: isDisabled }}
        confirmLoading={loading}
        onCancel={() => setVisible(false)}
      >
        <div>
          <span style={{ color: 'red'}}>*</span>
          Remarks
        </div>
        <TextArea
          onChange={(e) => handleRemarks(e.target.value)}
        />
      </Modal>
    </Layout>
  )
}
export default Payment
  